import { 
  BookOpen, GraduationCap, Users, Award, Calendar, Car, Brain, 
  Globe, Shield, Zap, Target, TrendingUp, Settings, MessageCircle 
} from 'lucide-react';
import { ProductConfigs } from './types';

export const expandedEducationSolutionsConfigs: ProductConfigs = {
  'language-learning-translation-services': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI-Powered Adaptive Learning',
        description: 'Advanced machine learning algorithms create personalized learning paths based on individual progress and learning style',
        metrics: '85% faster language acquisition',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Globe,
        title: 'Natural Language Processing Engine',
        description: 'Sophisticated NLP technology for real-time conversation analysis, pronunciation feedback, and contextual learning',
        metrics: '95% pronunciation accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Cultural Immersion & Context',
        description: 'Comprehensive cultural education with business etiquette, social norms, and cross-cultural communication training',
        metrics: 'Cultural competency',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Professional Language Specialization',
        description: 'Industry-specific language training for medical, legal, technical, and business professionals with certification',
        metrics: '50+ industry specializations',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: MessageCircle,
        title: 'Virtual Reality Language Immersion',
        description: 'Immersive VR environments for practical language application in simulated real-world scenarios',
        metrics: 'VR immersion technology',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Award,
        title: 'International Certification Programs',
        description: 'Globally recognized language certifications with preparation for TOEFL, IELTS, DELE, and other standards',
        metrics: 'Global certifications',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Learning Analytics',
        description: 'Comprehensive progress tracking with predictive analytics and personalized improvement recommendations',
        metrics: 'Predictive learning insights',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Real-Time Translation & Interpretation',
        description: 'Professional-grade translation services with real-time interpretation and document localization',
        metrics: 'Real-time translation',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Brain,
        title: 'Cognitive Load Optimization',
        description: 'AI-powered difficulty adjustment based on cognitive load assessment and learning capacity analysis',
        metrics: 'Optimized learning load',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Social Learning & Peer Collaboration',
        description: 'Interactive community features with language exchange, peer review, and collaborative learning projects',
        metrics: 'Social learning network',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Settings,
        title: 'Enterprise Learning Management',
        description: 'Corporate training integration with LMS compatibility, progress reporting, and team management tools',
        metrics: 'Enterprise integration',
        color: 'from-gray-500 to-gray-600'
      },
      {
        icon: Globe,
        title: 'Multi-Platform Accessibility',
        description: 'Cross-platform learning with mobile apps, web access, and offline capabilities for continuous learning',
        metrics: 'Universal accessibility',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Award,
        title: 'Adaptive Assessment Engine',
        description: 'Dynamic testing with adaptive difficulty, competency-based evaluation, and skill gap analysis',
        metrics: 'Adaptive assessments',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Target,
        title: 'Academic Research Integration',
        description: 'Research-backed methodologies with continuous improvement based on linguistic and pedagogical research',
        metrics: 'Research-driven approach',
        color: 'from-pink-500 to-red-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Student Data Privacy Protection',
        description: 'FERPA-compliant data protection with encrypted storage and secure access controls for educational records',
        metrics: 'Educational privacy standards',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'High-Performance Learning Engine',
        description: 'Optimized learning platform with sub-second response times and real-time content delivery',
        metrics: '<500ms response time',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Education Infrastructure',
        description: 'Cloud-native architecture supporting unlimited learners with auto-scaling and global content delivery',
        metrics: 'Unlimited learner capacity',
        color: 'from-emerald-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'International Compliance Standards',
        description: 'Multi-jurisdictional compliance with international education standards and cross-border data protection',
        metrics: 'Global education compliance',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Brain,
        title: 'AI Ethics & Learning Bias Prevention',
        description: 'Ethical AI implementation with bias detection and fair learning opportunity algorithms',
        metrics: 'Ethical learning AI',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Learning Outcome Optimization',
        description: 'Continuous performance optimization with learning outcome measurement and success rate analytics',
        metrics: 'Optimized learning outcomes',
        color: 'from-orange-500 to-pink-600'
      }
    ]
  },

  'learning-management-system': {
    keyBenefits: [
      {
        icon: BookOpen,
        title: 'AI-Enhanced Course Creation',
        description: 'Intelligent course builder with automatic content structuring, learning objective mapping, and assessment generation',
        metrics: '70% faster course creation',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Adaptive Learning Pathways',
        description: 'Personalized learning experiences that adapt to individual student pace, learning style, and knowledge gaps',
        metrics: 'Personalized learning paths',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Analytics & Insights',
        description: 'Comprehensive learning analytics with predictive student success modeling and intervention recommendations',
        metrics: 'Predictive success analytics',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Virtual Classroom Excellence',
        description: 'Immersive virtual learning environments with breakout rooms, interactive whiteboards, and collaborative tools',
        metrics: 'Virtual classroom innovation',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Award,
        title: 'Competency-Based Assessment',
        description: 'Skills-based evaluation with micro-credentials, digital badges, and competency tracking systems',
        metrics: 'Competency-based learning',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: MessageCircle,
        title: 'Social Learning Integration',
        description: 'Collaborative learning features with peer review, group projects, and knowledge sharing communities',
        metrics: 'Social learning engagement',
        color: 'from-pink-500 to-pink-600'
      },
      {
        icon: Settings,
        title: 'Institutional Integration Hub',
        description: 'Seamless integration with student information systems, library resources, and third-party educational tools',
        metrics: 'Complete integration ecosystem',
        color: 'from-indigo-500 to-indigo-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Intelligent Content Recommendation',
        description: 'AI-powered content suggestions based on learning objectives, student performance, and knowledge graph analysis',
        metrics: 'AI content curation',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Collaboration Tools',
        description: 'Live collaborative editing, instant messaging, video conferencing, and shared workspaces for group learning',
        metrics: 'Real-time collaboration',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Target,
        title: 'Gamification & Engagement Engine',
        description: 'Comprehensive gamification with achievement systems, leaderboards, and engagement tracking',
        metrics: '85% engagement increase',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Multi-Modal Learning Support',
        description: 'Support for diverse learning styles with visual, auditory, kinesthetic, and interactive content formats',
        metrics: 'Multi-modal accessibility',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Globe,
        title: 'Mobile-First Learning Experience',
        description: 'Native mobile applications with offline content sync, push notifications, and mobile-optimized assessments',
        metrics: 'Mobile learning excellence',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Advanced Proctoring & Security',
        description: 'AI-powered online proctoring with facial recognition, keystroke analysis, and academic integrity monitoring',
        metrics: 'Academic integrity assurance',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Accessibility & Inclusion Features',
        description: 'WCAG 2.1 AA compliance with screen reader support, closed captioning, and assistive technology integration',
        metrics: 'Universal accessibility',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Educational Data Protection',
        description: 'FERPA and GDPR compliant data handling with encrypted storage and secure student record management',
        metrics: 'Educational privacy compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'High-Performance Content Delivery',
        description: 'Global CDN with adaptive streaming, content caching, and optimized multimedia delivery',
        metrics: 'Global content performance',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Learning Infrastructure',
        description: 'Auto-scaling cloud architecture supporting unlimited concurrent users with 99.9% uptime guarantee',
        metrics: 'Unlimited user scalability',
        color: 'from-emerald-500 to-emerald-600'
      },
      {
        icon: Brain,
        title: 'Learning Analytics Privacy',
        description: 'Privacy-preserving analytics with differential privacy and anonymized learning pattern analysis',
        metrics: 'Privacy-preserving analytics',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Target,
        title: 'Performance Optimization Engine',
        description: 'Continuous performance monitoring with automated optimization and learning experience enhancement',
        metrics: 'Optimized learning performance',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'International Education Standards',
        description: 'Compliance with global education standards including IMS Global Learning Consortium specifications',
        metrics: 'Global education standards',
        color: 'from-cyan-500 to-cyan-600'
      }
    ]
  }
};